import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MapPin, Instagram, Phone, Mail, Share2, Disc3, ArrowLeft, Calendar, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { usePageMeta } from '@/hooks/usePageMeta';
import api from '@/services/api';
import DJGalleryTab from '@/components/DJGalleryTab';

// Dedicated, shareable public DJ profile (NOT a modal). Served by GET /api/djs/{slug}.
// Backend returns only public-safe, gated fields: phone appears only when public_phone
// is set, booking_email only when provided, instagram only when a valid https URL.
//
// Layout: masthead, then About, Appearing At, and Gallery stacked on one page
// (no tabs). Gallery posts come from GET /api/djs/{slug}/gallery
// (public, approved DJs only) or GET /api/users/me/dj-gallery when the logged-in owner
// is viewing their own profile (owner identity = an approved application whose
// public_profile_url matches this slug). Owner gets Add/Edit/Delete controls.
export default function PublicDJProfile() {
  const { slug } = useParams();
  const [dj, setDj] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | notfound | error
  const [errMsg, setErrMsg] = useState('');
  const [isOwner, setIsOwner] = useState(false);
  const [gallery, setGallery] = useState([]);

  usePageMeta(
    dj ? `${dj.dj_name} — DJ` : 'DJ Profile',
    dj ? (dj.bio ? dj.bio.slice(0, 160) : `${dj.dj_name} on SoundWolves.`) : 'SoundWolves DJ profile.',
    dj?.profile_picture ? { image: dj.profile_picture } : {}
  );

  useEffect(() => {
    let alive = true;
    setStatus('loading');
    setIsOwner(false);
    setGallery([]);
    api.get(`/djs/${slug}`)
      .then((d) => { if (alive) { setDj(d); setStatus('ok'); } })
      .catch((e) => {
        if (!alive) return;
        // 404 (removed / archived / pending / rejected / unknown) -> unavailable.
        if (/not found/i.test(e.message)) setStatus('notfound');
        else { setErrMsg(e.message || 'Failed to load profile'); setStatus('error'); }
      });
    return () => { alive = false; };
  }, [slug]);

  // Owner detection + gallery fetch (after the profile resolves).
  useEffect(() => {
    if (status !== 'ok') return;
    let alive = true;
    (async () => {
      let owner = false;
      if (api.isAuthenticated()) {
        try {
          const appv = await api.get('/users/me/dj-application');
          owner = appv?.status === 'approved' && appv?.public_profile_url === `/djs/${slug}`;
        } catch (_) { owner = false; }
      }
      if (!alive) return;
      setIsOwner(owner);
      try {
        const r = owner ? await api.get('/users/me/dj-gallery') : await api.get(`/djs/${slug}/gallery`);
        if (alive) setGallery(r?.posts || []);
      } catch (_) { if (alive) setGallery([]); }
    })();
    return () => { alive = false; };
  }, [status, slug]);

  // Reload used by the owner after a create/edit/delete.
  const reloadGallery = useCallback(async () => {
    try {
      const r = isOwner ? await api.get('/users/me/dj-gallery') : await api.get(`/djs/${slug}/gallery`);
      setGallery(r?.posts || []);
    } catch (_) { setGallery([]); }
  }, [isOwner, slug]);

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: dj?.dj_name, url });
      else { await navigator.clipboard.writeText(url); toast.success('Profile link copied'); }
    } catch { /* user dismissed share sheet */ }
  };

  if (status === 'loading') return <div className="max-w-4xl mx-auto px-4 py-20 text-center opacity-60">Loading profile…</div>;

  if (status === 'notfound') {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center">
        <Disc3 className="mx-auto mb-4 h-12 w-12 opacity-40" />
        <h1 className="text-2xl font-display font-bold mb-2">DJ profile unavailable</h1>
        <p className="text-muted-foreground mb-6">This DJ profile doesn’t exist or is no longer public.</p>
        <Link to="/djs"><Button variant="outline"><ArrowLeft className="w-4 h-4 mr-2" /> Browse DJs</Button></Link>
      </div>
    );
  }

  if (status === 'error') {
    return <div className="max-w-xl mx-auto px-4 py-20 text-center text-destructive">{errMsg}</div>;
  }

  // Portfolio link is rendered when the backend provides it (separate from gallery).
  const portfolioUrl = dj.portfolio_url || dj.portfolio_link || null;
  const PortfolioLink = () => (portfolioUrl ? (
    <a href={portfolioUrl} target="_blank" rel="noopener noreferrer" className="inline-block">
      <Button variant="outline" size="sm"><ExternalLink className="w-4 h-4 mr-2" /> Portfolio</Button>
    </a>
  ) : null);

  return (
    <div className="max-w-4xl mx-auto px-4 pt-20 pb-24 md:pb-8">
      <Link to="/djs" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="w-4 h-4 mr-1" /> All DJs
      </Link>

      {/* Masthead (unchanged) */}
      <div className="flex flex-col md:flex-row gap-6">
        <div className="w-full md:w-72 shrink-0">
          <div className="aspect-square w-full overflow-hidden rounded-xl bg-muted">
            {dj.profile_picture ? (
              <img src={dj.profile_picture} alt={dj.dj_name} className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            ) : (
              <div className="w-full h-full flex items-center justify-center"><Disc3 className="w-16 h-16 opacity-30" /></div>
            )}
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-3xl font-display font-bold">{dj.dj_name}</h1>
              {dj.city && (
                <p className="text-muted-foreground flex items-center gap-1 mt-1">
                  <MapPin className="w-4 h-4" /> {dj.city}
                </p>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={share}><Share2 className="w-4 h-4 mr-1" /> Share</Button>
          </div>

          {dj.genres?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {dj.genres.map((g) => <Badge key={g} variant="secondary" className="capitalize">{g}</Badge>)}
            </div>
          )}

          {/* Contact / links — only rendered when the backend includes the field. */}
          <div className="flex flex-wrap gap-2 mt-5">
            {dj.instagram && (
              <a href={dj.instagram} target="_blank" rel="noopener noreferrer">
                <Button variant="premium" size="sm"><Instagram className="w-4 h-4 mr-2" /> Follow on Instagram</Button>
              </a>
            )}
            {dj.booking_email && (
              <a href={`mailto:${dj.booking_email}`}>
                <Button variant="outline" size="sm"><Mail className="w-4 h-4 mr-2" /> Booking inquiry</Button>
              </a>
            )}
            {dj.phone && (
              <a href={`tel:${dj.phone}`}>
                <Button variant="outline" size="sm"><Phone className="w-4 h-4 mr-2" /> {dj.phone}</Button>
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Stacked sections (no tabs): About, Appearing At, Gallery */}
      <div className="mt-8">
        {portfolioUrl && <div className="mb-6"><PortfolioLink /></div>}

        {dj.bio && (
          <div className="mb-8">
            <h2 className="text-lg font-display font-bold mb-2">About</h2>
            <p className="whitespace-pre-wrap leading-relaxed text-foreground/90">{dj.bio}</p>
          </div>
        )}

        {dj.linked_events?.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-display font-bold mb-3">Appearing at</h2>
            <div className="space-y-2">
              {dj.linked_events.map((ev) => (
                <Card key={ev.event_id}>
                  <CardContent className="py-3 flex items-center gap-3">
                    <Calendar className="w-4 h-4 text-primary shrink-0" />
                    <div className="min-w-0">
                      <div className="font-medium truncate">{ev.title || 'Event'}</div>
                      <div className="text-sm text-muted-foreground truncate">
                        {[ev.date, ev.location].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        <div className="mb-2">
          <h2 className="text-lg font-display font-bold mb-3">Gallery</h2>
          <DJGalleryTab posts={gallery} isOwner={isOwner} onChanged={reloadGallery} />
        </div>
      </div>
    </div>
  );
}
